import { Spacing } from '@persona-id/design-tokens';
import { capitalize, snakeCase } from 'lodash/fp';
import { Controller, get, UseFormReturn } from 'react-hook-form';
import styled from 'styled-components';

import {
  DEFAULT_MAX_CHAR_LENGTH,
  DEFAULT_SANITIZE,
} from 'dashboard/apps/InquiryTemplateEditor/FieldPanel/shared';
import { FieldWrapper } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import { SUPPORTED_FIELD_TYPE } from 'dashboard/components/DocumentFields/util';
import { ErrorText } from 'design-system/components/FormControl';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Label } from 'design-system/components/Text';
import {
  FIELD_POLICY_OPTIONS,
  FIELD_SCHEMA_TYPE_DISPLAY,
  FieldSchema,
  FieldSchemaType,
} from 'lib/fields';

const Section = styled.section`
  padding: ${Spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const StyledLabel = styled(Label)`
  margin-bottom: ${Spacing[2]};
  display: inline-block;
`;

const FieldErrorText = ({ message }: { message?: string }) =>
  message ? (
    <>
      <Spacer height={0.5} />
      <ErrorText>{message}</ErrorText>
    </>
  ) : null;

interface DocumentFieldPanelContentProps {
  formReturn: UseFormReturn<FieldSchema, object>;
}

const DocumentFieldPanelContent = ({ formReturn }: DocumentFieldPanelContentProps) => {
  const { register, formState, watch, setValue, control } = formReturn;
  const { errors } = formState;
  const documentFieldType = watch('type');

  return (
    <>
      <Section>
        <StyledLabel>Name</StyledLabel>
        <Controller
          name='label'
          control={control}
          render={({ field }) => (
            <FieldWrapper>
              <Input
                {...field}
                placeholder='Enter document field'
                hasError={!!errors?.label}
                onChange={(e) => {
                  field.onChange(e);
                  setValue('key', snakeCase(e.target.value));
                }}
                autoFocus
              />
            </FieldWrapper>
          )}
        />
        <FieldErrorText message={errors?.label?.message} />
        <Spacer height={1.5} />
        <StyledLabel>Key</StyledLabel>
        <FieldWrapper>
          <Input {...register('key')} hasError={!!errors?.key} disabled />
        </FieldWrapper>
        <FieldErrorText message={errors?.key?.message} />
        <Spacer height={1.5} />
        <StyledLabel>Type</StyledLabel>
        <Controller
          name='type'
          control={control}
          render={({ field }) => (
            <FieldWrapper>
              <Select
                {...field}
                placeholder='Select type'
                hasError={!!errors?.type}
                onValueChange={(e) => {
                  field.onChange(e);
                  setValue(
                    'config.max-char-length',
                    e === FieldSchemaType.String ? DEFAULT_MAX_CHAR_LENGTH : null,
                  );
                  setValue(
                    'config.sanitize',
                    e === FieldSchemaType.String ? DEFAULT_SANITIZE : null,
                  );
                  setValue('default-value', null);
                }}
              >
                {SUPPORTED_FIELD_TYPE.map((fieldType) => (
                  <Option key={fieldType} value={fieldType}>
                    {FIELD_SCHEMA_TYPE_DISPLAY[fieldType]}
                  </Option>
                ))}
              </Select>
            </FieldWrapper>
          )}
        />
        <FieldErrorText message={errors?.type?.message} />
        <Controller
          name='default-value'
          control={control}
          render={({ field }) => {
            let input = undefined;
            switch (watch('type')) {
              case FieldSchemaType.Boolean:
                input = (
                  <Select
                    {...field}
                    placeholder='Select default value'
                    hasError={!!errors?.['default-value']}
                  >
                    {['true', 'false'].map((val) => (
                      <Option key={val} value={val}>
                        {capitalize(val)}
                      </Option>
                    ))}
                  </Select>
                );
                break;
              case FieldSchemaType.Integer:
              case FieldSchemaType.Number:
                input = (
                  <Input
                    {...field}
                    placeholder='Enter default value'
                    type='number'
                    hasError={!!errors?.['default-value']}
                    onChange={(e) => field.onChange(e.target.value === '' ? '' : +e.target.value)}
                  />
                );
                break;
              case FieldSchemaType.String:
                input = (
                  <Input
                    {...field}
                    placeholder='Enter default value'
                    hasError={!!errors?.['default-value']}
                  />
                );
                break;
            }

            if (input == null) {
              return <></>;
            }

            return (
              <>
                <Spacer height={1.5} />
                <StyledLabel>Default value (optional)</StyledLabel>
                <FieldWrapper>{input}</FieldWrapper>
                <FieldErrorText message={errors?.['default-value']?.message?.toString()} />
              </>
            );
          }}
        />
        {documentFieldType === FieldSchemaType.String && (
          <>
            <Spacer height={1.5} />
            <StyledLabel>Max length (char)</StyledLabel>
            <FieldWrapper>
              <Input
                {...register('config.max-char-length', {
                  valueAsNumber: true,
                })}
                placeholder='Enter max length'
                hasError={!!get(errors, 'config.max-char-length')}
              />
            </FieldWrapper>
            <FieldErrorText message={get(errors, 'config.max-char-length')?.message} />
          </>
        )}
        <Spacer height={1.5} />
        <StyledLabel>Redaction policy</StyledLabel>
        <Controller
          name='config.redaction-policy'
          control={control}
          render={({ field }) => (
            <FieldWrapper>
              <Select {...field} placeholder='Select redaction policy'>
                {FIELD_POLICY_OPTIONS['redaction-policy'].map(({ label, value }) => (
                  <Option key={value} value={value}>
                    {label}
                  </Option>
                ))}
              </Select>
            </FieldWrapper>
          )}
        />
        <Spacer height={1.5} />
        <StyledLabel>Write policy</StyledLabel>
        <Controller
          name='config.write-policy'
          control={control}
          render={({ field }) => (
            <FieldWrapper>
              <Select {...field} placeholder='Select write policy'>
                {FIELD_POLICY_OPTIONS['write-policy'].map(({ label, value }) => (
                  <Option key={value} value={value}>
                    {label}
                  </Option>
                ))}
              </Select>
            </FieldWrapper>
          )}
        />
      </Section>
    </>
  );
};

export default DocumentFieldPanelContent;
